import { experimental__simple as simple } from '@clerk/themes';
/**
 * Clerk appearance configuration aligned with the SiFly Joy UI theme (sifly.ts)
 * and Tailwind config.
 *
 * Design tokens referenced:
 *   Fonts       – "Be Vietnam Pro" (display/headings), "Barlow" (body)
 *   Primary     – yellow/gold spectrum (#F1E90A … #F57D00), text-on-primary = black
 *   Neutral     – #FBFCFE … #0B0D0E
 *   Danger      – #C41C1C
 *   Success     – #1F7A1F
 *   Border-rad  – Joy UI sm = 6px (buttons & inputs)
 *   Input height– Joy UI md = 2.5rem (40px)
 */

const clerkAppearance = {
  baseTheme: simple,
  variables: {
    // ── Colours ──────────────────────────────────────────────────────────
    colorPrimary: '#32383E',            // neutral-700-ish (closer to legacy login neutral button)
    colorDanger: '#C41C1C',             // danger-500
    colorSuccess: '#1F7A1F',            // success-500
    colorWarning: '#9A5B13',            // warning-500
    colorNeutral: '#171A1C',            // neutral-800  (darkest usable neutral)
    colorText: '#171A1C',               // neutral-800
    colorTextOnPrimaryBackground: '#FFFFFF',
    colorTextSecondary: '#636B74',      // neutral-500
    colorBackground: '#FFFFFF',         // light body
    colorInputBackground: '#FBFCFE',    // neutral-50 / surface
    colorInputText: '#171A1C',          // neutral-800

    // ── Typography ───────────────────────────────────────────────────────
    fontFamily: '"Barlow", sans-serif',
    fontFamilyButtons: '"Barlow", sans-serif',
    fontSize: '0.9375rem',              // 15px – matches Joy body-md default
    fontSmoothing: 'antialiased',

    // ── Shape ────────────────────────────────────────────────────────────
    borderRadius: '0',                // Joy UI --joy-radius-sm
    spacingUnit: '16px',
  },

  elements: {
    // ── Card / root container ────────────────────────────────────────────
    rootBox: {
      width: '100%',
      backgroundColor: '#000000',
    },
    cardBox: {
      boxShadow: 'none',
      backgroundColor: '#000000',
    },
    card: {
      boxShadow: 'none',
      border: 'none',
      backgroundColor: '#000000',
      borderRadius: '0',
    },

    // ── Header ───────────────────────────────────────────────────────────
    headerTitle: {
      display: 'none',
    },
    headerSubtitle: {
      display: 'none',
    },

    // ── Primary button (sign-in / continue) ─────────────────────────────
    formButtonPrimary: {
      fontFamily: '"Barlow", sans-serif',
      fontWeight: 400,
      fontSize: '0.9375rem',
      height: '3rem',
      borderRadius: '6px',
      backgroundColor: '#32383E',
      color: '#FFFFFF',
      textTransform: 'none' as const,
      boxShadow: 'none',
      transition: 'background-color 150ms ease, transform 150ms ease',
      '&:hover': {
        backgroundColor: '#171A1C',
      },
      '&:focus': {
        boxShadow: '0 0 0 3px rgba(50, 56, 62, 0.35)',
      },
      '&:active': {
        backgroundColor: '#171A1C',
      },
    },

    // ── Form inputs ──────────────────────────────────────────────────────
    formFieldInput: {
      fontFamily: '"Barlow", sans-serif',
      fontSize: '0.9375rem',
      height: '3rem',
      padding: '0.75rem 1rem',
      borderRadius: '6px',
      borderColor: '#CDD7E1',           // neutral-300
      backgroundColor: '#FBFCFE',       // neutral-50
      color: '#171A1C',                 // neutral-800
      transition: 'border-color 150ms ease, box-shadow 150ms ease',
      '&:focus': {
        borderColor: '#32383E',
        boxShadow: '0 0 0 3px rgba(50, 56, 62, 0.25)',
      },
    },

    formFieldLabel: {
      fontFamily: '"Barlow", sans-serif',
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#FBFCFE',
    },

    formFieldAction: {
      fontFamily: '"Barlow", sans-serif',
      color: '#F5C100',                 // primary-700
      fontWeight: 500,
      '&:hover': {
        color: '#F6A800',               // primary-800
      },
    },

    // ── Social / OAuth buttons ───────────────────────────────────────────
    socialButtonsBlockButton: {
      fontFamily: '"Barlow", sans-serif',
      height: '2.5rem',
      borderRadius: '6px',
      borderColor: '#CDD7E1',           // neutral-300
      backgroundColor: '#FFFFFF',
      transition: 'background-color 150ms ease, border-color 150ms ease',
      '&:hover': {
        backgroundColor: '#F0F4F8',     // neutral-100
        borderColor: '#9FA6AD',         // neutral-400
      },
    },
    socialButtonsBlockButtonText: {
      fontFamily: '"Barlow", sans-serif',
      fontWeight: 500,
      color: '#171A1C',                 // neutral-800
    },

    // ── Divider ──────────────────────────────────────────────────────────
    dividerLine: {
      backgroundColor: '#DDE7EE',       // neutral-200
    },
    dividerText: {
      fontFamily: '"Barlow", sans-serif',
      color: '#DDE7EE',
      fontSize: '0.8125rem',
    },

    // ── Footer / links ───────────────────────────────────────────────────
    footerAction: {
      backgroundColor: '#000000',
      '& *': {
        backgroundColor: '000000',
      },
      display: 'block',
      width: '100%',
      textAlign: 'center',
    },
    footerActionText: {
      color: '#FBFCFE',
    },
    footerActionLink: {
      fontFamily: '"Barlow", sans-serif',
      color: '#F5C100',                 // primary-700
      fontWeight: 500,
      marginLeft: '0.5rem',
      '&:hover': {
        color: '#F6A800',               // primary-800
      },
    },
    footer: {
      backgroundColor: '#000000',
      '& *': {
        backgroundColor: '#000000',
      },
      '& + div': {
        display: 'none',
      },
    },

    // ── Identity preview (shown after email entry) ───────────────────────
    identityPreviewText: {
      fontFamily: '"Barlow", sans-serif',
      color: '#FBFCFE',
    },
    identityPreviewEditButton: {
      color: '#F5C100',                 // primary-700
      '&:hover': {
        color: '#F6A800',               // primary-800
      },
    },

    // ── Alert / error banners ────────────────────────────────────────────
    alert: {
      borderRadius: '6px',
    },

    // ── OTP input ────────────────────────────────────────────────────────
    otpCodeFieldInput: {
      fontFamily: '"Barlow", sans-serif',
      borderRadius: '6px',
      borderColor: '#CDD7E1',
      '&:focus': {
        borderColor: '#F5D907',
        boxShadow: '0 0 0 3px rgba(245, 217, 7, 0.25)',
      },
    },
  },
};

export default clerkAppearance;
